import { useEffect, useCallback } from 'react';
import { QueryStatus } from '@reduxjs/toolkit/query';

import { getErrorMessage } from 'utils';
import { HandleMutationProps, BaseHandleMutationProps } from 'types';

import { useAlert } from './alert';

export const useBaseHandleMutation = <T extends Record<string, any>>({
  data,
  error,
  reset,
  status,
  onError,
  onSuccess,
  showError,
}: BaseHandleMutationProps<T>) => {
  useEffect(() => {
    if (error) {
      if (onError) {
        onError(getErrorMessage(error), error);
      } else {
        showError(getErrorMessage(error));
      }
    }
  }, [error, onError, showError]);

  useEffect(() => {
    if (status === QueryStatus.fulfilled) {
      if (onSuccess) {
        onSuccess(data);
      }
      if (reset) {
        reset();
      }
    }
  }, [status, onSuccess, reset, data]);
};

export const useHandleMutation = <T extends Record<string, any>>(
  params: HandleMutationProps<T> & { successMessage?: string }
) => {
  const { showErrorAlert, showSuccessAlert } = useAlert();

  useBaseHandleMutation<T>({
    ...params,
    showError: showErrorAlert,
    onSuccess: useCallback(
      (...successParams: [data?: T | void]) => {
        params.onSuccess?.(...successParams);

        if (params.successMessage) {
          showSuccessAlert(params.successMessage);
        }
      },
      [params, showSuccessAlert]
    ),
  });
};
